<template>
  <Teleport defer to="ion-app">
    <ion-modal
      id="timeout-modal"
      ref="modal"
      :swipe-to-close="false"
      :can-dismiss="false"
      class="timeout-modal"
      @ionModalDidDismiss="onModalDismiss"
    >
      <div class="timeout-modal-wrapper">
        <div class="timeout-modal-content">
          <p class="timeout-modal-text">
            Oops, there's something wrong with the connection 🥲 Reload and try again!
          </p>
          <button class="timeout-modal-button" @click="reloadApp">Reload</button>
        </div>
      </div>
    </ion-modal>
  </Teleport>
</template>

<script>
import { IonModal } from '@ionic/vue';

export default {
  name: 'TimeoutModal',
  components: {
    IonModal,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.$refs.modal?.$el?.present();
      } else {
        this.$refs.modal?.$el?.dismiss();
      }
    },
  },
  mounted() {
    if (this.isOpen) {
      this.$nextTick(() => this.$refs.modal?.$el?.present());
    }
  },
  methods: {
    async reloadApp() {
      try {
        await this.$refs.modal?.$el?.dismiss?.();
      } catch (error) {
        console.warn('[TimeoutModal] Error dismissing modal:', error);
      }
      // Small delay to ensure modal closes
      await new Promise(resolve => setTimeout(resolve, 100));
      // Emit retry event - parent will also set isOpen to false
      this.$emit('retry');
    },
    onModalDismiss() {
      // Handle modal dismissal if needed
      // This ensures modal closes properly
    },
  },
};
</script>

<style scoped>
ion-modal#timeout-modal.timeout-modal {
  --width: 327px;
  --height: fit-content;
  --border-radius: 12px;
  z-index: 9999; /* Ensure modal appears above splash screen */
  /* Gray backdrop overlay - semi-transparent so you can see through */
  --backdrop-opacity: 0.4;
  --ion-backdrop-color: rgba(0, 0, 0, 0.4);
}

/* Ensure modal wrapper is visible */
.timeout-modal-wrapper {
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  position: relative;
  z-index: 10000;
  pointer-events: none; /* Allow clicks to pass through wrapper */
  background: transparent;
  margin: 0;
  overflow: hidden;
}

.timeout-modal-content {
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  width: 327px;
  padding: 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  box-shadow: 0px 4px 20px 4px rgba(0, 0, 0, 0.3);
  border: 2px solid rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10001;
  pointer-events: auto; /* Re-enable pointer events on content */
}

.timeout-modal-text {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 25px;
  color: #303030;
  text-align: center;
  margin: 0;
  width: 100%;
}

.timeout-modal-button {
  background: #fec629;
  border: none;
  border-radius: 100px;
  padding: 12px 20px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  color: #21201e;
  text-align: center;
  cursor: pointer;
  letter-spacing: -0.48px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.timeout-modal-button:active {
  opacity: 0.8;
}
</style>

<style>
/* Global styles for ion-modal to ensure visibility */
ion-modal#timeout-modal.timeout-modal {
  --backdrop-opacity: 0.4 !important;
  --ion-backdrop-color: rgba(0, 0, 0, 0.4) !important;
  /* Force remove any yellow background from modal itself */
  background: transparent !important;
  background-color: transparent !important;
  --background: transparent !important;
  --ion-background-color: transparent !important;
}

/* Target the shadow DOM root to remove yellow background */
ion-modal#timeout-modal.timeout-modal::part(backdrop),
ion-modal#timeout-modal.timeout-modal::part(content) {
  background: transparent !important;
  background-color: transparent !important;
}

/* Ensure modal content wrapper is visible and transparent */
ion-modal#timeout-modal.timeout-modal .modal-wrapper {
  background: transparent !important;
  background-color: transparent !important;
  --background: transparent !important;
  --ion-background-color: transparent !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  height: 100% !important;
  overflow: visible !important; /* Changed to visible so backdrop can show through */
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
  max-height: 100% !important;
  pointer-events: none !important; /* Allow backdrop clicks */
}

/* Remove any default Ionic modal styling that might cause yellow stripe */
ion-modal#timeout-modal.timeout-modal {
  --width: 100% !important;
  --max-width: 100% !important;
}

/* Ensure wrapper and modal elements are transparent, but keep content white */
ion-modal#timeout-modal.timeout-modal .modal-wrapper,
ion-modal#timeout-modal.timeout-modal .timeout-modal-wrapper {
  background-color: transparent !important;
  background: transparent !important;
  --background: transparent !important;
  --ion-background-color: transparent !important;
}

/* Force remove yellow from modal shadow DOM and all children */
/* But exclude the content box which should be white */
ion-modal#timeout-modal.timeout-modal .ion-delegate-host,
ion-modal#timeout-modal.timeout-modal .ion-page {
  background-color: transparent !important;
  background: transparent !important;
  --background: transparent !important;
  --ion-background-color: transparent !important;
}

/* Re-apply white background to content */
ion-modal#timeout-modal.timeout-modal .timeout-modal-content {
  background-color: #ffffff !important;
  background: #ffffff !important;
}

/* Gray backdrop overlay - semi-transparent gray filter */
/* Set opacity directly on the backdrop element - Ionic multiplies backdrop-opacity with element opacity */
ion-modal#timeout-modal.timeout-modal ion-backdrop {
  background: rgba(0, 0, 0, 1) !important; /* Full black, then use opacity to make transparent */
  background-color: rgba(0, 0, 0, 1) !important;
  --background: rgba(0, 0, 0, 1) !important;
  --ion-backdrop-color: rgba(0, 0, 0, 1) !important;
  /* Set opacity directly - this is what makes it semi-transparent */
  opacity: 0.4 !important; /* 40% opacity = 60% transparent */
  /* Force remove any yellow tint */
  filter: none !important;
  mix-blend-mode: normal !important;
  display: block !important;
  visibility: visible !important;
}

/* Override any yellow that might be inherited */
ion-modal#timeout-modal.timeout-modal ion-backdrop::before,
ion-modal#timeout-modal.timeout-modal ion-backdrop::after {
  background: rgba(0, 0, 0, 0.3) !important;
  background-color: rgba(0, 0, 0, 0.3) !important;
  opacity: 0.3 !important;
}

/* Ensure modal content is visible */
ion-modal#timeout-modal.timeout-modal .timeout-modal-content {
  background: #ffffff !important;
  position: relative !important;
  z-index: 10001 !important;
  box-shadow: 0px 4px 20px 4px rgba(0, 0, 0, 0.3) !important;
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
}
</style>
